import React, { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { selectArticles } from '../../../redux/selectors';
import { setArticles } from '../../../redux/actions';

import StatTable from '../../base/megatable/stat-table';
import ColumnsDropdown from '../../base/dropdowns/columns-dropdown';
import DateRangesDropdown from '../../base/dropdowns/dateranges-dropdown';
import Pagination from '../../base/common/pagination';
import { Columns } from '../../base/megatable/column-service';
import { Loader } from '../../misc/loader-utils';
import { calculatePagination } from '../../base/common/utils';
import { api } from '../../../services/api';
import { dateService } from '../../../services/date';
import { session } from '../../../services/session';
import ReportDropdown from '../../base/dropdowns/report-dropdown';
import WinningDropdown from './winning-dropdown';

// Table variables
const statType = 'merchantArticleSearch';
const tableName = 'Merchant Article Search';
const sortDisabledFields = ['is_winning', 'pub_name', 'edit_name', 'products_count'];
const defaultWinning = [
  { label: 'Winning', name: 'winning', selected: true },
  { label: 'Not Winning', name: 'not winning', selected: true },
];
const defaultRanges = [{ label: 'Last 30 Days', name: 'last30', selected: true }];
const range = 'last30';
const limit = 500;
let previousChips = null;

function ArticleSearchTable({ orgId, filter, chips, history, match, location }) {
  // State
  const [loading, setLoading] = useState(true);
  const [winningItems, setWinningItems] = useState(defaultWinning);
  const [dateRanges] = useState(defaultRanges);
  const [rowCounts, setRowCounts] = useState({ visible: 0, all: 0 });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  const baseColumns = new Columns().getColumnsForTable(statType);
  const tableColumns = session.allSites ? baseColumns.slice(1) : baseColumns;
  const defaultColumns = cloneDeep(tableColumns);
  const [columns, setColumns] = useState(tableColumns);
  const [sort, setSort] = useState({ order_by: 'clicks', direction: 'desc' });

  // Reset pagination on new search
  if (chips !== previousChips) setPage(1);
  previousChips = chips;

  // Redux
  const dispatch = useDispatch();
  const winning = getWinning();
  const statKey = `${chips.join(',')},${range},${sort.order_by},${sort.direction},${winning}`;
  const dataEndpoint = `/api/v0/merchants/${orgId}/article_search/search/`;
  const [backendPage, idxStart, idxEnd] = calculatePagination(page, perPage, limit);
  const [articles, totalItems] = useSelector(state =>
    selectArticles(state, statKey, backendPage, idxStart, idxEnd),
  );

  // Effects
  useEffect(() => {
    if (!articles.length) {
      fetchData();
    } else {
      updateRowCounts(articles, totalItems);
      setLoading(false);
    }
  }, [chips, page, perPage, range, winning, sort]);

  // Methods
  async function fetchData() {
    setLoading(true);

    const params = getQueryParams();
    const resp = await api.get(dataEndpoint, params);
    const articles = resp.data.data[0].stats;
    const totalItems = resp.data.data[0].page_info.total_items;

    dispatch(setArticles(articles, totalItems, statKey, backendPage));
    updateRowCounts(articles, totalItems);
    setLoading(false);
  }

  function getQueryParams() {
    const winning = winningItems.filter(item => item.selected);
    const { thirtyDay } = dateService.getPresetDateRanges();
    const encodedChips = chips.map(chip => encodeURIComponent(chip));
    const params = {
      date_from: dateService.formatDate(thirtyDay.startDate),
      date_to: dateService.formatDate(thirtyDay.endDate),
      search_query: encodedChips.join(','),
      type: filter,
      order_by: sort.order_by,
      direction: sort.direction,
      page: backendPage,
      per_page: limit,
      use_v2: session.isAdmin,
    };

    if (winning.length === 1) {
      params.is_winning = winning[0].name === 'winning';
    }

    return params;
  }

  function getWinning() {
    const selections = winningItems.filter(item => item.selected);
    return selections.length === 1 ? selections[0].name : 'all';
  }

  function updateRowCounts(articles, totalItems) {
    setRowCounts({
      visible: articles.length <= perPage ? articles.length : perPage,
      all: totalItems,
    });
  }

  function updateSortParams(name) {
    if (sortDisabledFields.includes(name)) return;

    const sortName = name === 'revenue_range' ? 'total_attributed_revenue' : name;
    const isNewColumn = sort.order_by !== sortName;
    setPage(1);
    setSort({
      order_by: isNewColumn ? sortName : sort.order_by,
      direction: isNewColumn ? 'desc' : sort.direction === 'desc' ? 'asc' : 'desc',
    });
  }

  // Render
  const table = !loading ? (
    <StatTable
      stats={articles}
      columns={columns}
      sortDisabledFields={sortDisabledFields}
      updateSortParams={name => updateSortParams(name)}
      statType={statType}
      tableName={tableName}
      sortOptions={sort}
      disableEditUrl={true}
      history={history}
      match={match}
      location={location}
      classOverride="base-search-table article-search-table"
    ></StatTable>
  ) : null;

  const winningDropdown = !session.allSites ? (
    <WinningDropdown
      items={winningItems}
      updateOptions={setWinningItems}
      gaEvent={{ category: 'Merchant Article Search', label: 'Winning Dropdown' }}
    />
  ) : null;

  const searchDescriptor = chips.length > 1 ? `+ ${chips.length - 1} more terms` : '';

  const tableHeader = !loading ? (
    <div className="header">
      <div className="table-name">
        <span className="title">Top Performing Articles</span>
        <span className="search">
          containing "{chips[0]}" {searchDescriptor}
        </span>
      </div>
      <div className="dropdowns">
        {winningDropdown}
        <ColumnsDropdown
          gaEvent={{ category: 'Merchant Article Search', label: 'Columns' }}
          staticFields={[]}
          items={columns}
          updateColumns={options => setColumns(options)}
          resetColumns={() => setColumns(defaultColumns)}
          statType={statType}
        />
        <DateRangesDropdown
          items={dateRanges}
          gaEvent={{ category: 'Merchant Article Search', label: 'Date Range' }}
          updateRange={() => {}}
        />
        <ReportDropdown
          statType={statType}
          orgId={orgId}
          gaEvent={{
            category: 'Article Search',
            label: `${tableName} Export Data`,
          }}
          queryParams={getQueryParams()}
          columns={columns}
          currentPage={page}
          perPage={perPage}
          rowCounts={rowCounts}
          disabled={loading}
        />
      </div>
    </div>
  ) : null;

  return (
    <div className="result">
      <Loader loading={loading} />
      {tableHeader}
      {table}
      <Pagination
        showPagination={!loading}
        currentPage={page}
        totalItems={totalItems}
        perPage={perPage}
        eventPrefix={`${statType} Mega Tables`}
        updatePage={item => setPage(item)}
        updatePerPage={item => setPerPage(item)}
      ></Pagination>
    </div>
  );
}

export default ArticleSearchTable;
